import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import styles from './SpotlightSearch.module.css';

const SpotlightSearch = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState({
    products: [],
    ingredients: [],
    recipes: [],
    stores: [],
    items: [],
    vendors: []
  });
  
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const db = getFirestore();

  // Fetch all data when component mounts
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        // Fetch products
        const productsSnap = await getDocs(collection(db, 'products'));
        const products = productsSnap.docs.map(doc => ({
          id: doc.id,
          type: 'product',
          ...doc.data()
        }));

        // Fetch ingredients
        const ingredientsSnap = await getDocs(collection(db, 'ingredients'));
        
        // Fetch stores
        const storesSnap = await getDocs(collection(db, 'stores'));
        const stores = storesSnap.docs.map(doc => ({
          id: doc.id,
          type: 'store',
          ...doc.data()
        }));

        // Fetch items
        const itemsSnap = await getDocs(collection(db, 'items'));
        
        // Fetch vendors
        const vendorsSnap = await getDocs(collection(db, 'vendors'));
        const vendors = vendorsSnap.docs.map(doc => ({
          id: doc.id,
          type: 'vendor',
          ...doc.data()
        }));

        // Map store names to ingredients
        const ingredients = ingredientsSnap.docs.map(doc => {
          const data = doc.data();
          const store = stores.find(s => s.id === data.storeId);
          return {
            id: doc.id,
            type: 'ingredient',
            ...data,
            storeName: store?.name || 'Unknown Store'
          };
        });

        // Extract recipes from products
        const recipes = [];
        products.forEach(product => {
          if (product.recipes) {
            product.recipes.forEach((recipe, index) => {
              recipes.push({
                id: `${product.id}-${index}`,
                type: 'recipe',
                recipeName: recipe.recipeName,
                productName: product.name,
                productId: product.id,
                recipeIndex: index,
                isFinal: recipe.isFinal || false,
                basePackets: recipe.basePackets || 1,
                ingredients: recipe.ingredients || [],
                ingredientCount: recipe.ingredients ? recipe.ingredients.length : 0
              });
            });
          }
        });

        // Add ingredient counts to stores
        const storesWithCounts = stores.map(store => ({
          ...store,
          ingredientCount: ingredients.filter(ing => ing.storeId === store.id).length
        }));

        // Map vendor names to items and add counts
        const items = itemsSnap.docs.map(doc => {
          const data = doc.data();
          const vendor = vendors.find(v => v.id === data.vendorId);
          return {
            id: doc.id,
            type: 'item',
            ...data,
            vendorName: vendor?.name || 'Unknown Vendor'
          };
        });

        // Add item counts to vendors
        const vendorsWithCounts = vendors.map(vendor => ({
          ...vendor,
          itemCount: items.filter(item => item.vendorId === vendor.id).length
        }));

        setAllData({ products, ingredients, recipes, stores: storesWithCounts, items, vendors: vendorsWithCounts });
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchAllData();
    }
  }, [isOpen, db]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Search function
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setSelectedIndex(0);
      return;
    }

    const searchTerm = query.toLowerCase();
    const searchResults = [];

    // Search products
    allData.products.forEach(product => {
      if (product.name?.toLowerCase().includes(searchTerm)) {
        searchResults.push({
          ...product,
          title: product.name,
          subtitle: 'Product',
          icon: '📦'
        });
      }
    });

    // Search ingredients
    allData.ingredients.forEach(ingredient => {
      if (ingredient.name?.toLowerCase().includes(searchTerm)) {
        const currentStock = ingredient.currentStock || 0;
        const unit = ingredient.unit || 'units';
        searchResults.push({
          ...ingredient,
          title: ingredient.name,
          subtitle: `Ingredient • ${ingredient.storeName || 'Unknown store'} • Stock: ${currentStock} ${unit}`,
          icon: '🥄'
        });
      }
    });

    // Search recipes
    allData.recipes.forEach(recipe => {
      if (recipe.recipeName?.toLowerCase().includes(searchTerm) || 
          recipe.productName?.toLowerCase().includes(searchTerm)) {
        const ingredientText = recipe.ingredientCount === 1 ? '1 ingredient' : `${recipe.ingredientCount} ingredients`;
        searchResults.push({
          ...recipe,
          title: recipe.recipeName,
          subtitle: `Recipe • ${recipe.productName}${recipe.isFinal ? ' (Final)' : ''} • ${ingredientText}`,
          icon: '📋'
        });
      }
    });

    // Search stores
    allData.stores.forEach(store => {
      if (store.name?.toLowerCase().includes(searchTerm)) {
        const ingredientText = store.ingredientCount === 1 ? '1 ingredient' : `${store.ingredientCount} ingredients`;
        searchResults.push({
          ...store,
          title: store.name,
          subtitle: `Store • ${ingredientText}`,
          icon: '🏪'
        });
      }
    });

    // Search items
    allData.items.forEach(item => {
      if (item.name?.toLowerCase().includes(searchTerm) || 
          item.vendorName?.toLowerCase().includes(searchTerm) ||
          item.category?.toLowerCase().includes(searchTerm)) {
        const currentStock = item.currentStock || 0;
        const unit = item.unit || 'units';
        searchResults.push({
          ...item,
          title: item.name,
          subtitle: `Item • ${item.vendorName || 'Unknown vendor'} • Stock: ${currentStock} ${unit}`,
          icon: '📦'
        });
      }
    });

    // Search vendors
    allData.vendors.forEach(vendor => {
      if (vendor.name?.toLowerCase().includes(searchTerm)) {
        const itemText = vendor.itemCount === 1 ? '1 item' : `${vendor.itemCount} items`;
        searchResults.push({
          ...vendor,
          title: vendor.name,
          subtitle: `Vendor • ${itemText}`,
          icon: '🏢'
        });
      }
    });

    // Sort results by relevance (exact matches first, then partial matches)
    searchResults.sort((a, b) => {
      const aExact = a.title.toLowerCase() === searchTerm;
      const bExact = b.title.toLowerCase() === searchTerm;
      
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      
      const aStarts = a.title.toLowerCase().startsWith(searchTerm);
      const bStarts = b.title.toLowerCase().startsWith(searchTerm);
      
      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;
      
      return a.title.localeCompare(b.title);
    });

    setResults(searchResults.slice(0, 10)); // Limit to 10 results
    setSelectedIndex(0);
  }, [query, allData]);

  // Handle item selection
  const handleItemSelect = useCallback((item) => {
    onClose();
    setQuery('');
    
    switch (item.type) {
      case 'product':
        navigate(`/products/${item.id}`);
        break;
      case 'ingredient':
        // Navigate to ingredients dashboard and highlight the ingredient
        navigate('/ingredients', { 
          state: { highlightId: item.id, highlightName: item.name } 
        });
        break;
      case 'recipe':
        // Navigate to product details and highlight the recipe
        navigate(`/products/${item.productId}`, { 
          state: { highlightRecipe: item.recipeIndex, recipeName: item.title } 
        });
        break;
      case 'store':
        // Navigate to stores dashboard and highlight the store
        navigate('/stores', { 
          state: { highlightId: item.id, highlightName: item.name } 
        });
        break;
      case 'item':
        // Navigate to items dashboard and highlight the item
        navigate('/items', { 
          state: { highlightId: item.id, highlightName: item.name } 
        });
        break;
      case 'vendor':
        // Navigate to vendors dashboard and highlight the vendor
        navigate('/vendors', { 
          state: { highlightId: item.id, highlightName: item.name } 
        });
        break;
      default:
        break;
    }
  }, [navigate, onClose]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < results.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            handleItemSelect(results[selectedIndex]);
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose, handleItemSelect]);

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.searchContainer} onClick={e => e.stopPropagation()}>
        <div className={styles.searchBox}>
          <div className={styles.searchIcon}>🔍</div>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products, ingredients, recipes, stores, items, vendors..."
            className={styles.searchInput}
          />
          {loading && <div className={styles.loadingSpinner}>⏳</div>}
        </div>
        
        {results.length > 0 && (
          <div className={styles.resultsContainer}>
            {results.map((result, index) => (
              <div
                key={`${result.type}-${result.id}`}
                className={`${styles.resultItem} ${
                  index === selectedIndex ? styles.selected : ''
                }`}
                onClick={() => handleItemSelect(result)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className={styles.resultIcon}>{result.icon}</div>
                <div className={styles.resultContent}>
                  <div className={styles.resultTitle}>{result.title}</div>
                  <div className={styles.resultSubtitle}>{result.subtitle}</div>
                </div>
                <div className={styles.resultType}>
                  {result.type === 'recipe' && result.isFinal && (
                    <span className={styles.finalBadge}>FINAL</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {query && results.length === 0 && !loading && (
          <div className={styles.noResults}>
            <div className={styles.noResultsIcon}>🔍</div>
            <div className={styles.noResultsText}>No results found</div>
            <div className={styles.noResultsSubtext}>
              Try searching for products, ingredients, recipes, or stores
            </div>
          </div>
        )}
        
        <div className={styles.footer}>
          <div className={styles.footerHints}>
            <span className={styles.hint}>
              <kbd>↑</kbd><kbd>↓</kbd> to navigate
            </span>
            <span className={styles.hint}>
              <kbd>↵</kbd> to select
            </span>
            <span className={styles.hint}>
              <kbd>esc</kbd> to close
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpotlightSearch;
